// Static reference data for the Phase 2 mock (spec §15): banks and branches,
// countries with IBAN/blocked flags, recharge and bill lists, FX rates and the
// fixed name pools used by the lookup handlers. Everything is deterministic so
// the web and mobile mocks behave the same.

export interface BankSeed {
  code: string;
  name: string;
  ifscPrefix: string;
  branches: Array<[name: string, city: string]>;
}

// 43 Indian banks, 3–4 branches each. IFSC = <PREFIX>000000N (1-based by branch).
export const BANKS: BankSeed[] = [
  { code: 'NOVA', name: 'NovaBank', ifscPrefix: 'NOVA', branches: [['Fort', 'Mumbai'], ['Connaught Place', 'New Delhi'], ['MG Road', 'Bengaluru'], ['Anna Salai', 'Chennai']] },
  { code: 'HDFC', name: 'HDFC Bank', ifscPrefix: 'HDFC', branches: [['Sandoz House', 'Mumbai'], ['Koramangala', 'Bengaluru'], ['Sector 17', 'Chandigarh']] },
  { code: 'ICIC', name: 'ICICI Bank', ifscPrefix: 'ICIC', branches: [['Bandra Kurla', 'Mumbai'], ['Gachibowli', 'Hyderabad'], ['Salt Lake', 'Kolkata']] },
  { code: 'SBIN', name: 'State Bank of India', ifscPrefix: 'SBIN', branches: [['Main Branch', 'Mumbai'], ['Parliament Street', 'New Delhi'], ['Park Street', 'Kolkata'], ['T Nagar', 'Chennai']] },
  { code: 'UTIB', name: 'Axis Bank', ifscPrefix: 'UTIB', branches: [['Worli', 'Mumbai'], ['Viman Nagar', 'Pune'], ['Ashram Road', 'Ahmedabad']] },
  { code: 'KKBK', name: 'Kotak Mahindra Bank', ifscPrefix: 'KKBK', branches: [['Lower Parel', 'Mumbai'], ['Jayanagar', 'Bengaluru'], ['Nungambakkam', 'Chennai']] },
  { code: 'INDB', name: 'IndusInd Bank', ifscPrefix: 'INDB', branches: [['Andheri East', 'Mumbai'], ['Baner', 'Pune'], ['Gomti Nagar', 'Lucknow']] },
  { code: 'YESB', name: 'Yes Bank', ifscPrefix: 'YESB', branches: [['Santacruz West', 'Mumbai'], ['Nehru Place', 'New Delhi'], ['Hitech City', 'Hyderabad']] },
  { code: 'PUNB', name: 'Punjab National Bank', ifscPrefix: 'PUNB', branches: [['Karol Bagh', 'New Delhi'], ['Mall Road', 'Amritsar'], ['Hazratganj', 'Lucknow']] },
  { code: 'BARB', name: 'Bank of Baroda', ifscPrefix: 'BARB', branches: [['Alkapuri', 'Vadodara'], ['Dadar West', 'Mumbai'], ['C Scheme', 'Jaipur']] },
  { code: 'CNRB', name: 'Canara Bank', ifscPrefix: 'CNRB', branches: [['Town Hall', 'Bengaluru'], ['Mylapore', 'Chennai'], ['Aminabad', 'Lucknow']] },
  { code: 'UBIN', name: 'Union Bank of India', ifscPrefix: 'UBIN', branches: [['Nariman Point', 'Mumbai'], ['Abids', 'Hyderabad'], ['Boring Road', 'Patna']] },
  { code: 'BKID', name: 'Bank of India', ifscPrefix: 'BKID', branches: [['Ballard Estate', 'Mumbai'], ['Civil Lines', 'Nagpur'], ['MG Marg', 'Indore']] },
  { code: 'IOBA', name: 'Indian Overseas Bank', ifscPrefix: 'IOBA', branches: [['Mount Road', 'Chennai'], ['Thrissur Round', 'Thrissur'], ['Rajaji Salai', 'Chennai']] },
  { code: 'CBIN', name: 'Central Bank of India', ifscPrefix: 'CBIN', branches: [['Fort', 'Mumbai'], ['Arera Hills', 'Bhopal'], ['Bistupur', 'Jamshedpur']] },
  { code: 'MAHB', name: 'Bank of Maharashtra', ifscPrefix: 'MAHB', branches: [['Shivajinagar', 'Pune'], ['Sitabuldi', 'Nagpur'], ['Station Road', 'Aurangabad']] },
  { code: 'UCBA', name: 'UCO Bank', ifscPrefix: 'UCBA', branches: [['BBD Bagh', 'Kolkata'], ['Naya Sarak', 'Cuttack'], ['Rajpur Road', 'Dehradun']] },
  { code: 'PSIB', name: 'Punjab & Sind Bank', ifscPrefix: 'PSIB', branches: [['Rajendra Place', 'New Delhi'], ['Model Town', 'Ludhiana'], ['Gandhi Nagar', 'Jammu']] },
  { code: 'IDIB', name: 'Indian Bank', ifscPrefix: 'IDIB', branches: [['Rajaji Salai', 'Chennai'], ['Cantt Road', 'Lucknow'], ['MG Road', 'Thiruvananthapuram']] },
  { code: 'FDRL', name: 'Federal Bank', ifscPrefix: 'FDRL', branches: [['Aluva', 'Kochi'], ['Kadavanthra', 'Kochi'], ['Pattom', 'Thiruvananthapuram']] },
  { code: 'SIBL', name: 'South Indian Bank', ifscPrefix: 'SIBL', branches: [['Round South', 'Thrissur'], ['Panampilly Nagar', 'Kochi'], ['Hampankatta', 'Mangaluru']] },
  { code: 'KVBL', name: 'Karur Vysya Bank', ifscPrefix: 'KVBL', branches: [['East Street', 'Karur'], ['Saibaba Colony', 'Coimbatore'], ['KK Nagar', 'Madurai']] },
  { code: 'CIUB', name: 'City Union Bank', ifscPrefix: 'CIUB', branches: [['Big Bazaar Street', 'Kumbakonam'], ['Kamarajar Salai', 'Madurai'], ['Trichy Main Road', 'Thanjavur']] },
  { code: 'TMBL', name: 'Tamilnad Mercantile Bank', ifscPrefix: 'TMBL', branches: [['Beach Road', 'Tuticorin'], ['Palayamkottai', 'Tirunelveli'], ['Anna Nagar', 'Madurai']] },
  { code: 'CSBK', name: 'CSB Bank', ifscPrefix: 'CSBK', branches: [['Round West', 'Thrissur'], ['MG Road', 'Kochi'], ['YMCA Cross Road', 'Kozhikode']] },
  { code: 'DCBL', name: 'DCB Bank', ifscPrefix: 'DCBL', branches: [['Juhu', 'Mumbai'], ['Banjara Hills', 'Hyderabad'], ['Chetpet', 'Chennai']] },
  { code: 'RBL', name: 'RBL Bank', ifscPrefix: 'RATN', branches: [['Kolhapur Main', 'Kolhapur'], ['FC Road', 'Pune'], ['Vashi', 'Navi Mumbai']] },
  { code: 'IDFB', name: 'IDFC First Bank', ifscPrefix: 'IDFB', branches: [['BKC', 'Mumbai'], ['Jubilee Hills', 'Hyderabad'], ['Indiranagar', 'Bengaluru']] },
  { code: 'AUBL', name: 'AU Small Finance Bank', ifscPrefix: 'AUBL', branches: [['Malviya Nagar', 'Jaipur'], ['Sodala', 'Jaipur'], ['Paota', 'Jodhpur']] },
  { code: 'ESAF', name: 'ESAF Small Finance Bank', ifscPrefix: 'ESMF', branches: [['Thrissur', 'Thrissur'], ['Palakkad', 'Palakkad'], ['Ernakulam', 'Kochi']] },
  { code: 'UJJV', name: 'Ujjivan Small Finance Bank', ifscPrefix: 'UJVN', branches: [['Koramangala', 'Bengaluru'], ['Tollygunge', 'Kolkata'], ['Guindy', 'Chennai']] },
  { code: 'EQUT', name: 'Equitas Small Finance Bank', ifscPrefix: 'ESFB', branches: [['Guindy', 'Chennai'], ['RS Puram', 'Coimbatore'], ['Nizamabad', 'Nizamabad']] },
  { code: 'JANA', name: 'Jana Small Finance Bank', ifscPrefix: 'JSFB', branches: [['HSR Layout', 'Bengaluru'], ['Karol Bagh', 'New Delhi'], ['Kothrud', 'Pune']] },
  { code: 'NESF', name: 'North East Small Finance Bank', ifscPrefix: 'NESF', branches: [['GS Road', 'Guwahati'], ['Zoo Road', 'Guwahati'], ['MG Avenue', 'Imphal']] },
  { code: 'AIRP', name: 'Airtel Payments Bank', ifscPrefix: 'AIRP', branches: [['Udyog Vihar', 'Gurugram'], ['Kalyani Nagar', 'Pune'], ['Madhapur', 'Hyderabad']] },
  { code: 'IPOS', name: 'India Post Payments Bank', ifscPrefix: 'IPOS', branches: [['GPO', 'New Delhi'], ['GPO', 'Mumbai'], ['GPO', 'Chennai']] },
  { code: 'FINO', name: 'Fino Payments Bank', ifscPrefix: 'FINO', branches: [['Andheri East', 'Mumbai'], ['Karol Bagh', 'New Delhi'], ['Secunderabad', 'Hyderabad']] },
  { code: 'PYTM', name: 'Paytm Payments Bank', ifscPrefix: 'PYTM', branches: [['Sector 62', 'Noida'], ['Salt Lake Sector V', 'Kolkata'], ['BTM Layout', 'Bengaluru']] },
  { code: 'JIOP', name: 'Jio Payments Bank', ifscPrefix: 'JIOP', branches: [['Ghansoli', 'Navi Mumbai'], ['Gachibowli', 'Hyderabad'], ['Electronic City', 'Bengaluru']] },
  { code: 'NSDL', name: 'NSDL Payments Bank', ifscPrefix: 'NSPB', branches: [['Lower Parel', 'Mumbai'], ['Connaught Place', 'New Delhi'], ['Camac Street', 'Kolkata']] },
  { code: 'SCBL', name: 'Standard Chartered Bank', ifscPrefix: 'SCBL', branches: [['Nariman Point', 'Mumbai'], ['DLF Cyber City', 'Gurugram'], ['Camac Street', 'Kolkata']] },
  { code: 'HSBC', name: 'HSBC India', ifscPrefix: 'HSBC', branches: [['Fort', 'Mumbai'], ['Brigade Road', 'Bengaluru'], ['Nungambakkam', 'Chennai']] },
  { code: 'CITI', name: 'Citibank India', ifscPrefix: 'CITI', branches: [['BKC', 'Mumbai'], ['DLF Phase 2', 'Gurugram'], ['Aundh', 'Pune']] },
];

export const OPERATORS = ['Jio', 'Airtel', 'Vi', 'BSNL'];

export const CIRCLES = [
  'Andhra Pradesh', 'Assam', 'Bihar & Jharkhand', 'Chennai', 'Delhi NCR', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jammu & Kashmir', 'Karnataka', 'Kerala', 'Kolkata',
  'Madhya Pradesh & Chhattisgarh', 'Maharashtra & Goa', 'Mumbai', 'North East', 'Odisha',
  'Punjab', 'Rajasthan', 'Tamil Nadu', 'Uttar Pradesh (East)', 'Uttar Pradesh (West)',
  'West Bengal',
];

export const BILL_CATEGORIES = [
  'Electricity', 'Gas', 'Water', 'Broadband', 'DTH', 'Postpaid', 'FASTag',
  'Insurance', 'Loan EMI', 'Municipal Tax',
];

export const BILLERS: Record<string, string[]> = {
  Electricity: ['Adani Electricity Mumbai', 'BESCOM Bengaluru', 'MSEDCL Maharashtra', 'Tata Power Delhi', 'TNEB Tamil Nadu'],
  Gas: ['Adani Total Gas', 'Gujarat Gas', 'IGL Delhi', 'Mahanagar Gas', 'SPNGRL'],
  Water: ['Brihanmumbai Water Supply', 'BWSSB Bengaluru', 'Delhi Jal Board', 'Hyderabad Water Board', 'Chennai Metro Water'],
  Broadband: ['ACT Fibernet', 'Airtel Broadband', 'BSNL Broadband', 'Jio Fiber', 'Vi Broadband'],
  DTH: ['Airtel Digital TV', 'Dish TV', 'Sun Direct', 'Tata Play', 'd2h'],
  Postpaid: ['Airtel Postpaid', 'BSNL Postpaid', 'Jio Postpaid', 'Vi Postpaid'],
  FASTag: ['HDFC FASTag', 'ICICI FASTag', 'IDFC FASTag', 'Paytm FASTag', 'SBI FASTag'],
  Insurance: ['HDFC Life', 'ICICI Prudential', 'LIC of India', 'Max Life', 'SBI Life'],
  'Loan EMI': ['Bajaj Finance', 'HDFC Loans', 'ICICI Loans', 'Kotak Loans', 'Tata Capital'],
  'Municipal Tax': ['BMC Mumbai', 'BBMP Bengaluru', 'MCGM Property Tax', 'NDMC Delhi', 'PMC Pune'],
};

export const LRS_PURPOSES: Array<{ label: string; code: string }> = [
  { label: 'Family maintenance', code: 'S1301' },
  { label: 'Education', code: 'S0305' },
  { label: 'Medical treatment', code: 'S1108' },
  { label: 'Travel', code: 'S0301' },
  { label: 'Gift', code: 'S1302' },
  { label: 'Investment abroad', code: 'S0021' },
];

/** FX rates in paise per one unit of foreign currency (spec §15). */
export const FX_RATES_PAISE: Record<string, number> = {
  USD: 8410, EUR: 9130, GBP: 10650, AED: 2290, SGD: 6240,
  AUD: 5520, CAD: 6080, JPY: 56, CHF: 9510, SAR: 2240,
};

export const INTERNATIONAL_CHARGE_RUPEES = 590;

/** Fixed name pool for mobile lookups (picked by last two digits) and for
 *  account-name lookups (picked deterministically from the account number). */
export const LOOKUP_NAMES = [
  'RAHUL SHARMA', 'PRIYA NAIR', 'AMIT PATEL', 'SNEHA IYER', 'VIKRAM SINGH',
  'NEHA GUPTA', 'ARJUN MEHTA', 'DIVYA REDDY', 'KARAN MALHOTRA', 'ANANYA DAS',
  'ROHAN VERMA', 'ISHA KULKARNI', 'ADITYA JOSHI', 'MEERA MENON', 'NITIN AGARWAL',
  'POOJA BHATT', 'SANJAY KUMAR', 'RITU CHOPRA', 'MANOJ PILLAI', 'TARA SEN',
];

export const SAMPLE_QRS: Array<{ pa: string; pn: string; am?: number }> = [
  { pa: 'chaipoint@ybl', pn: 'CHAI POINT' },
  { pa: 'freshmart@okaxis', pn: 'FRESH MART' },
  { pa: 'cityautos@okhdfc', pn: 'CITY AUTOS', am: 250 },
];

export const LOCATE_US: Array<{ name: string; kind: 'Branch' | 'ATM'; address: string }> = [
  { name: 'NovaBank Fort', kind: 'Branch', address: 'Ground Floor, 24 Horniman Circle, Fort, Mumbai 400001' },
  { name: 'NovaBank Connaught Place', kind: 'Branch', address: 'G-7, Inner Circle, Connaught Place, New Delhi 110001' },
  { name: 'NovaBank MG Road', kind: 'Branch', address: 'Prestige Towers, MG Road, Bengaluru 560001' },
  { name: 'Fort ATM', kind: 'ATM', address: 'Horniman Circle, Fort, Mumbai 400001' },
  { name: 'Indiranagar ATM', kind: 'ATM', address: '100 Feet Road, Indiranagar, Bengaluru 560038' },
  { name: 'CP ATM', kind: 'ATM', address: 'Outer Circle, Connaught Place, New Delhi 110001' },
];

// Countries the mock blocks (spec §6.3).
export const BLOCKED_COUNTRIES = new Set(['KP', 'IR', 'MM']);

// EU members (IBAN required) + AE, GB, SA, QA (spec §6.3).
export const IBAN_REQUIRED = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
  'SE', 'AE', 'GB', 'SA', 'QA',
]);

const EU_CURRENCIES = new Set(IBAN_REQUIRED);

/** ISO 3166 country list (India excluded — it cannot be an international
 *  transfer destination). code, name. Currency is derived below. */
const COUNTRY_NAMES: Array<[string, string]> = [
  ['AF', 'Afghanistan'], ['AL', 'Albania'], ['DZ', 'Algeria'], ['AD', 'Andorra'],
  ['AO', 'Angola'], ['AG', 'Antigua and Barbuda'], ['AR', 'Argentina'], ['AM', 'Armenia'],
  ['AU', 'Australia'], ['AT', 'Austria'], ['AZ', 'Azerbaijan'], ['BS', 'Bahamas'],
  ['BH', 'Bahrain'], ['BD', 'Bangladesh'], ['BB', 'Barbados'], ['BY', 'Belarus'],
  ['BE', 'Belgium'], ['BZ', 'Belize'], ['BJ', 'Benin'], ['BT', 'Bhutan'],
  ['BO', 'Bolivia'], ['BA', 'Bosnia and Herzegovina'], ['BW', 'Botswana'], ['BR', 'Brazil'],
  ['BN', 'Brunei'], ['BG', 'Bulgaria'], ['BF', 'Burkina Faso'], ['BI', 'Burundi'],
  ['KH', 'Cambodia'], ['CM', 'Cameroon'], ['CA', 'Canada'], ['CV', 'Cape Verde'],
  ['CF', 'Central African Republic'], ['TD', 'Chad'], ['CL', 'Chile'], ['CN', 'China'],
  ['CO', 'Colombia'], ['KM', 'Comoros'], ['CG', 'Congo'], ['CD', 'Congo (DRC)'],
  ['CR', 'Costa Rica'], ['HR', 'Croatia'], ['CU', 'Cuba'], ['CY', 'Cyprus'],
  ['CZ', 'Czechia'], ['DK', 'Denmark'], ['DJ', 'Djibouti'], ['DM', 'Dominica'],
  ['DO', 'Dominican Republic'], ['EC', 'Ecuador'], ['EG', 'Egypt'], ['SV', 'El Salvador'],
  ['GQ', 'Equatorial Guinea'], ['ER', 'Eritrea'], ['EE', 'Estonia'], ['SZ', 'Eswatini'],
  ['ET', 'Ethiopia'], ['FJ', 'Fiji'], ['FI', 'Finland'], ['FR', 'France'],
  ['GA', 'Gabon'], ['GM', 'Gambia'], ['GE', 'Georgia'], ['DE', 'Germany'],
  ['GH', 'Ghana'], ['GR', 'Greece'], ['GD', 'Grenada'], ['GT', 'Guatemala'],
  ['GN', 'Guinea'], ['GW', 'Guinea-Bissau'], ['GY', 'Guyana'], ['HT', 'Haiti'],
  ['HN', 'Honduras'], ['HK', 'Hong Kong'], ['HU', 'Hungary'], ['IS', 'Iceland'],
  ['ID', 'Indonesia'], ['IR', 'Iran'], ['IQ', 'Iraq'], ['IE', 'Ireland'],
  ['IL', 'Israel'], ['IT', 'Italy'], ['CI', 'Ivory Coast'], ['JM', 'Jamaica'],
  ['JP', 'Japan'], ['JO', 'Jordan'], ['KZ', 'Kazakhstan'], ['KE', 'Kenya'],
  ['KI', 'Kiribati'], ['KP', 'North Korea'], ['KR', 'South Korea'], ['KW', 'Kuwait'],
  ['KG', 'Kyrgyzstan'], ['LA', 'Laos'], ['LV', 'Latvia'], ['LB', 'Lebanon'],
  ['LS', 'Lesotho'], ['LR', 'Liberia'], ['LY', 'Libya'], ['LI', 'Liechtenstein'],
  ['LT', 'Lithuania'], ['LU', 'Luxembourg'], ['MO', 'Macau'], ['MG', 'Madagascar'],
  ['MW', 'Malawi'], ['MY', 'Malaysia'], ['MV', 'Maldives'], ['ML', 'Mali'],
  ['MT', 'Malta'], ['MH', 'Marshall Islands'], ['MR', 'Mauritania'], ['MU', 'Mauritius'],
  ['MX', 'Mexico'], ['FM', 'Micronesia'], ['MD', 'Moldova'], ['MC', 'Monaco'],
  ['MN', 'Mongolia'], ['ME', 'Montenegro'], ['MA', 'Morocco'], ['MZ', 'Mozambique'],
  ['MM', 'Myanmar'], ['NA', 'Namibia'], ['NR', 'Nauru'], ['NP', 'Nepal'],
  ['NL', 'Netherlands'], ['NZ', 'New Zealand'], ['NI', 'Nicaragua'], ['NE', 'Niger'],
  ['NG', 'Nigeria'], ['MK', 'North Macedonia'], ['NO', 'Norway'], ['OM', 'Oman'],
  ['PK', 'Pakistan'], ['PW', 'Palau'], ['PA', 'Panama'], ['PG', 'Papua New Guinea'],
  ['PY', 'Paraguay'], ['PE', 'Peru'], ['PH', 'Philippines'], ['PL', 'Poland'],
  ['PT', 'Portugal'], ['QA', 'Qatar'], ['RO', 'Romania'], ['RU', 'Russia'],
  ['RW', 'Rwanda'], ['WS', 'Samoa'], ['SM', 'San Marino'], ['ST', 'Sao Tome and Principe'],
  ['SA', 'Saudi Arabia'], ['SN', 'Senegal'], ['RS', 'Serbia'], ['SC', 'Seychelles'],
  ['SL', 'Sierra Leone'], ['SG', 'Singapore'], ['SK', 'Slovakia'], ['SI', 'Slovenia'],
  ['SB', 'Solomon Islands'], ['SO', 'Somalia'], ['ZA', 'South Africa'], ['ES', 'Spain'],
  ['LK', 'Sri Lanka'], ['SD', 'Sudan'], ['SR', 'Suriname'], ['SE', 'Sweden'],
  ['CH', 'Switzerland'], ['SY', 'Syria'], ['TW', 'Taiwan'], ['TJ', 'Tajikistan'],
  ['TZ', 'Tanzania'], ['TH', 'Thailand'], ['TL', 'Timor-Leste'], ['TG', 'Togo'],
  ['TO', 'Tonga'], ['TT', 'Trinidad and Tobago'], ['TN', 'Tunisia'], ['TR', 'Turkey'],
  ['TM', 'Turkmenistan'], ['UG', 'Uganda'], ['UA', 'Ukraine'], ['AE', 'United Arab Emirates'],
  ['GB', 'United Kingdom'], ['US', 'United States'], ['UY', 'Uruguay'], ['UZ', 'Uzbekistan'],
  ['VU', 'Vanuatu'], ['VE', 'Venezuela'], ['VN', 'Vietnam'], ['YE', 'Yemen'],
  ['ZM', 'Zambia'], ['ZW', 'Zimbabwe'],
];

const COUNTRY_CURRENCY: Record<string, string> = {
  US: 'USD', GB: 'GBP', AE: 'AED', SG: 'SGD', AU: 'AUD',
  CA: 'CAD', JP: 'JPY', CH: 'CHF', SA: 'SAR',
};

export interface CountrySeed {
  code: string;
  name: string;
  currency: string;
  ibanRequired: boolean;
  blocked: boolean;
}

export const COUNTRIES: CountrySeed[] = COUNTRY_NAMES.map(([code, name]) => ({
  code,
  name,
  currency: COUNTRY_CURRENCY[code] ?? (EU_CURRENCIES.has(code) ? 'EUR' : 'USD'),
  ibanRequired: IBAN_REQUIRED.has(code),
  blocked: BLOCKED_COUNTRIES.has(code),
}));

/** Routing-code metadata by country (spec §6.3). */
export const ROUTING_CODES: Record<string, { label: string; digits: number } | { label: string; optional: true }> = {
  US: { label: 'ABA routing number', digits: 9 },
  GB: { label: 'Sort code', digits: 6 },
  AU: { label: 'BSB', digits: 6 },
  CA: { label: 'Transit and institution number', digits: 8 },
};
export const DEFAULT_ROUTING = { label: 'Bank code (optional)', optional: true as const };

export const LEGAL_DOCUMENTS: Record<string, string> = {
  terms: [
    'NovaBank — Terms and Conditions',
    'These terms govern your use of NovaBank mobile and internet banking. By registering you agree to keep your credentials confidential, to use the services only for lawful purposes and to review your statements regularly.',
    'NovaBank may vary these terms with notice. Transactions initiated with your MPIN, OTP or UPI PIN are treated as authorised by you. Report any unauthorised transaction to 1800 266 4321 immediately.',
    'Accounts are governed by RBI guidelines and the Banking Regulation Act. Deposits are insured up to the limit prescribed by DICGC.',
  ].join('\n\n'),
  privacy: [
    'NovaBank — Privacy Commitment',
    'We collect only the information needed to open and run your account: identity documents, contact details and transaction records. We do not sell your data.',
    'Data is shared with regulators and service providers strictly as required by law, and stored in India in line with RBI data-localisation directions.',
    'You may write to customercare@novabank.in to access, correct or erase your personal data subject to statutory retention requirements.',
  ].join('\n\n'),
  safety: [
    'NovaBank — Safety & Security Usage Guidelines',
    'Never share your MPIN, OTP, UPI PIN or passwords with anyone, including people claiming to be from NovaBank. Bank staff will never ask for them.',
    'Always check the payee name shown after verification before approving a payment. If a UPI ID is flagged as reported, pay only if you know the person.',
    'Log out after each session, keep your device locked, and report suspicious activity to 1800 266 4321 (toll free), 8 AM – 8 PM, Monday to Saturday.',
  ].join('\n\n'),
};
